import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { API, http, formatDate, imgSrc } from "@/lib/api";
import { Reveal, SectionLabel } from "./Reveal";

export const PostCover = ({ post, className = "aspect-[4/3]" }) =>
  post?.cover_image ? (
    <div className={`relative overflow-hidden bg-[#EFE9DC] ${className}`}>
      <img
        src={imgSrc(post.cover_image)}
        alt={post.title}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
        data-testid={`post-cover-${post.slug}`}
      />
      <span className="absolute inset-0 bg-[#16302B]/10 transition-opacity duration-700 group-hover:opacity-0" />
    </div>
  ) : null;

export const PostCard = ({ post, i }) => (
  <Reveal delay={i * 0.1} as="article" className="group">
    <Link to={`/blog/${post.slug}`} className="block" data-testid={`post-card-${post.slug}`}>
      <PostCover post={post} className="aspect-[4/3] mb-6" />
      <div className="border-t border-[#221F1B] pt-6">
      <div className="flex items-center justify-between text-[12px] tracking-wide text-[#5C564E]">
        <span className="eyebrow !text-[10px]">{post.category}</span>
        <span>{post.reading_minutes} min read</span>
      </div>
      <h3 className="font-display mt-5 text-2xl sm:text-[1.7rem] leading-snug text-[#221F1B] transition-colors duration-300 group-hover:text-[#16302B]">
        <span className="bg-[linear-gradient(#B8874A,#B8874A)] bg-no-repeat bg-[length:0%_1px] bg-[position:0_100%] transition-[background-size] duration-700 group-hover:bg-[length:100%_1px]">{post.title}</span>
      </h3>
      <p className="mt-4 text-[15px] leading-[1.75] text-[#5C564E]">{post.excerpt}</p>
      <p className="mt-6 inline-flex items-center gap-2 text-[13px] text-[#16302B]">
        {formatDate(post.created_at)}
        <ArrowRight className="h-4 w-4 text-[#B8874A] transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} />
      </p>
      </div>
    </Link>
  </Reveal>
);

export const JournalTeaser = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    if (!API) return undefined;
    let alive = true;
    http.get("/posts?limit=3").then((r) => {
      if (alive) setPosts(Array.isArray(r?.data) ? r.data : []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!posts.length) return null;

  return (
    <section id="blog" className="relative bg-[#EFE9DC] py-24 sm:py-32 border-t border-[#D8CFBC]" data-testid="journal-teaser-section">
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <Reveal><SectionLabel index="VI.">Insights</SectionLabel></Reveal>
            <Reveal as="h2" className="font-display mt-8 text-4xl sm:text-5xl leading-[1.12] text-[#221F1B]">
              Notes from the <span className="italic text-[#16302B]">operating floor.</span>
            </Reveal>
          </div>
          <Link to="/blog" className="link-underline text-[14px] text-[#16302B] self-start sm:self-auto" data-testid="journal-view-all-link">
            View all articles
          </Link>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
          {posts.map((p, i) => <PostCard key={p.id} post={p} i={i} />)}
        </div>
      </div>
    </section>
  );
};
